import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { VocalConfiguration, BlendConfig, BlendedVocalOutput, VocalPack, ScreamConfiguration, DeepDiveConfiguration, EmotionalArcPoint, SunoDirectExport } from '@/types';
import { useVocalPacks } from '@hooks/useVocalPacks';
import { getScreamType, generateScreamPhysicsBlock } from '@data/extremeVocals';
import { getDialectById } from '@data/dialectModifiers';
import { getAgingProfileById } from '@data/agingProfiles';
import { getBreathPatternById } from '@data/breathPatterns';
import { getEmotionalArcById } from '@data/emotionalArcs';
import { useUndoStack } from '@hooks/useUndoStack';

interface VocalConfigContextType {
  config: VocalConfiguration;
  setSinglePack: (packKey: string) => void;
  setVoiceSpec: (spec: 'lead' | 'altA' | 'altB') => void;
  setHarmonyStack: (enabled: boolean) => void;
  setChoirGang: (enabled: boolean) => void;
  applyBlend: (blendConfig: BlendConfig, blendOutput: BlendedVocalOutput) => void;
  clearBlend: () => void;
  setScream: (updates: Partial<ScreamConfiguration>) => void;
  setDeepDive: (updates: Partial<DeepDiveConfiguration>) => void;
  setEmotionalArcFromEnergy: (segments: { position: number; energy: number }[]) => void;
  getActiveVocalArchitecture: () => string;
  getSunoDirectExport: () => SunoDirectExport | null;
  isBlendActive: boolean;
  undoVocal: () => void;
  redoVocal: () => void;
  canUndoVocal: boolean;
  canRedoVocal: boolean;
}

const defaultConfig: VocalConfiguration = {
  mode: 'single',
  singlePackKey: 'Southern Outlaw Pack',
  voiceSpec: 'lead',
  includeHarmonyStack: true,
  includeChoirGang: true,
  blendConfig: null,
  blendOutput: null,
  scream: { typeId: null, intensityId: 'full' },
  deepDive: { dialectId: null, agingId: null, breathPatternId: null, emotionalArcId: null, customArc: null }
};

const VocalConfigContext = createContext<VocalConfigContextType | null>(null);

export function VocalConfigProvider({ children }: { children?: ReactNode }) {
  const { data: vocalPacks } = useVocalPacks();
  const [config, setConfig] = useState<VocalConfiguration>(defaultConfig);
  const vocalUndo = useUndoStack<VocalConfiguration>({ maxHistory: 30 });

  useEffect(() => {
    vocalUndo.pushState(config);
  }, [config]);

  const undoVocal = useCallback(() => {
    const state = vocalUndo.undo();
    if (state) setConfig(state);
  }, [vocalUndo]);

  const redoVocal = useCallback(() => {
    const state = vocalUndo.redo();
    if (state) setConfig(state);
  }, [vocalUndo]);

  const setSinglePack = useCallback((packKey: string) => {
    setConfig(prev => ({
      ...prev,
      mode: 'single',
      singlePackKey: packKey,
      blendConfig: null,
      blendOutput: null
    }));
  }, []);

  const setVoiceSpec = useCallback((spec: 'lead' | 'altA' | 'altB') => {
    setConfig(prev => ({ ...prev, voiceSpec: spec }));
  }, []);

  const setHarmonyStack = useCallback((enabled: boolean) => {
    setConfig(prev => ({ ...prev, includeHarmonyStack: enabled }));
  }, []);

  const setChoirGang = useCallback((enabled: boolean) => {
    setConfig(prev => ({ ...prev, includeChoirGang: enabled }));
  }, []);

  const applyBlend = useCallback((blendConfig: BlendConfig, blendOutput: BlendedVocalOutput) => {
    setConfig(prev => ({
      ...prev,
      mode: 'blend',
      blendConfig,
      blendOutput
    }));
  }, []);

  const clearBlend = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      mode: 'single',
      blendConfig: null,
      blendOutput: null
    }));
  }, []);

  const setScream = useCallback((updates: Partial<ScreamConfiguration>) => {
    setConfig(prev => ({
      ...prev,
      scream: { ...prev.scream, ...updates }
    }));
  }, []);

  const setDeepDive = useCallback((updates: Partial<DeepDiveConfiguration>) => {
    setConfig(prev => ({
      ...prev,
      deepDive: { ...prev.deepDive, ...updates }
    }));
  }, []);

  const setEmotionalArcFromEnergy = useCallback((segments: { position: number; energy: number }[]) => {
    const getLabel = (energy: number, prev: number | null): string => {
      const delta = prev !== null ? energy - prev : 0;
      if (energy >= 85) return 'Explosive';
      if (energy >= 70 && delta > 10) return 'Surging';
      if (energy >= 70) return 'Powerful';
      if (energy >= 55 && delta > 10) return 'Building';
      if (energy >= 55) return 'Driving';
      if (energy >= 40 && delta < -10) return 'Retreating';
      if (energy >= 40) return 'Steady';
      if (energy >= 25) return 'Restrained';
      if (energy >= 10) return 'Whisper';
      return 'Silent';
    };

    const arcPoints: EmotionalArcPoint[] = segments.map((seg, i) => ({
      position: seg.position,
      emotion: getLabel(seg.energy, i > 0 ? segments[i - 1].energy : null),
      intensity: seg.energy
    }));

    setConfig(prev => ({
      ...prev,
      deepDive: { ...prev.deepDive, customArc: arcPoints, emotionalArcId: null }
    }));
  }, []);

  const getActiveVocalArchitecture = useCallback((): string => {
    const screamBlock = config.scream.typeId
      ? generateScreamPhysicsBlock(getScreamType(config.scream.typeId)!, config.scream.intensityId)
      : '';

    if (config.mode === 'blend' && config.blendOutput) {
      return buildBlendedArchitecture(config, screamBlock);
    } else if (config.singlePackKey) {
      return buildSinglePackArchitecture(config, screamBlock, vocalPacks);
    }
    return '';
  }, [config, vocalPacks]);

  const getSunoDirectExport = useCallback((): SunoDirectExport | null => {
    const deepDive = config.deepDive;
    const deepDiveSunoTags: string[] = [];
    const deepDiveExcludeTags: string[] = [];

    if (deepDive.dialectId) {
      const d = getDialectById(deepDive.dialectId);
      if (d) {
        deepDiveSunoTags.push(...d.sunoTags);
        deepDiveExcludeTags.push(...d.excludeTags);
      }
    }
    if (deepDive.agingId) {
      const a = getAgingProfileById(deepDive.agingId);
      if (a) deepDiveSunoTags.push(...a.sunoTags);
    }
    if (deepDive.breathPatternId) {
      const b = getBreathPatternById(deepDive.breathPatternId);
      if (b) deepDiveSunoTags.push(...b.sunoTags);
    }
    if (deepDive.emotionalArcId) {
      const e = getEmotionalArcById(deepDive.emotionalArcId);
      if (e) deepDiveSunoTags.push(...e.sunoTags);
    }

    let baseStyles = '';
    let baseExcludes = '';

    if (config.mode === 'blend' && config.blendOutput) {
      baseStyles = config.blendOutput.styles;
      baseExcludes = config.blendOutput.excludeStyles;
      // Blend output sunoStyleTags are already merged
      const blendTags = config.blendOutput.sunoStyleTags || [];
      const allTags = [...new Set([...blendTags, ...deepDiveSunoTags])];
      baseStyles = allTags.length > 0 ? allTags.join(', ') : baseStyles;
    } else if (config.singlePackKey) {
      const pack = vocalPacks[config.singlePackKey];
      if (!pack) return null;
      baseStyles = pack.styles;
      baseExcludes = pack.excludeStyles;
      if (deepDiveSunoTags.length > 0) {
        const packTags = baseStyles.split(',').map(t => t.trim()).filter(Boolean);
        const allTags = [...new Set([...packTags, ...deepDiveSunoTags])];
        baseStyles = allTags.join(', ');
      }
    } else {
      return null;
    }

    if (deepDiveExcludeTags.length > 0) {
      const existingExcludes = baseExcludes.split(',').map(t => t.trim()).filter(Boolean);
      const allExcludes = [...new Set([...existingExcludes, ...deepDiveExcludeTags])];
      baseExcludes = allExcludes.join(', ');
    }

    const physicsBlock = getActiveVocalArchitecture();
    const tagsList = baseStyles.split(',').map(t => t.trim()).filter(Boolean);
    const charCounts = {
      styleTags: baseStyles.length,
      excludeTags: baseExcludes.length,
      physicsBlock: physicsBlock.length
    };

    return {
      styleTags: baseStyles,
      excludeTags: baseExcludes,
      physicsBlock,
      tagsList,
      charCounts,
      isOverLimit: charCounts.styleTags > 1000 || charCounts.excludeTags > 500 || charCounts.physicsBlock > 5000
    };
  }, [config, getActiveVocalArchitecture]);

  const isBlendActive = config.mode === 'blend' && config.blendOutput !== null;

  return (
    <VocalConfigContext.Provider value={{
      config,
      setSinglePack,
      setVoiceSpec,
      setHarmonyStack,
      setChoirGang,
      applyBlend,
      clearBlend,
      setScream,
      setDeepDive,
      setEmotionalArcFromEnergy,
      getActiveVocalArchitecture,
      getSunoDirectExport,
      isBlendActive,
      undoVocal,
      redoVocal,
      canUndoVocal: vocalUndo.canUndo,
      canRedoVocal: vocalUndo.canRedo,
    }}>
      {children}
    </VocalConfigContext.Provider>
  );
}

export function useVocalConfig() {
  const context = useContext(VocalConfigContext);
  if (!context) throw new Error('useVocalConfig must be used within VocalConfigProvider');
  return context;
}

function buildDeepDiveBlock(deepDive: DeepDiveConfiguration): string {
  const blocks: string[] = [];

  if (deepDive.dialectId) {
    const d = getDialectById(deepDive.dialectId);
    if (d) blocks.push(d.physicsPrompt);
  }
  if (deepDive.agingId) {
    const a = getAgingProfileById(deepDive.agingId);
    if (a) blocks.push(a.physicsPrompt);
  }
  if (deepDive.breathPatternId) {
    const b = getBreathPatternById(deepDive.breathPatternId);
    if (b) blocks.push(b.physicsPrompt);
  }
  if (deepDive.emotionalArcId) {
    const e = getEmotionalArcById(deepDive.emotionalArcId);
    if (e) blocks.push(e.physicsPrompt);
  }

  if (blocks.length === 0) return '';
  return `\n{DEEP-DIVE VOCAL LAYERS}\n${blocks.join('\n\n')}`;
}

function buildSinglePackArchitecture(config: VocalConfiguration, screamBlock: string, packs: Record<string, VocalPack>): string {
  if (!config.singlePackKey) return '';
  const pack = packs[config.singlePackKey];
  if (!pack) return '';
  
  // Strip existing header from data to prevent double headers
  const cleanPhysics = pack.physics.replace(/\{LAYER-1 VOCAL PHYSICS\}\n?/gi, '').trim();
  
  let physicsBlock = `{LAYER-1 VOCAL PHYSICS}\n`;
  physicsBlock += `[Vocal_Behavior: ${cleanPhysics}]\n`;
  
  if (config.voiceSpec === 'lead') physicsBlock += `[Lead_Vocal: ${pack.voiceLead}]\n`;
  else if (config.voiceSpec === 'altA' && pack.voiceAltA) physicsBlock += `[Alt_A: ${pack.voiceAltA}]\n`;
  else if (config.voiceSpec === 'altB' && pack.voiceAltB) physicsBlock += `[Alt_B: ${pack.voiceAltB}]\n`;

  if (config.includeHarmonyStack) physicsBlock += `[Harmony: ${pack.harmonyStack}]\n`;
  if (config.includeChoirGang) physicsBlock += `[Choir: ${pack.gangVocals}]\n`;
  
  if (screamBlock) physicsBlock += `\n${screamBlock}\n`;

  const deepDivePhysics = buildDeepDiveBlock(config.deepDive);
  if (deepDivePhysics) physicsBlock += `\n${deepDivePhysics}\n`;

  return `[VOCALFORGE ARCHITECTURE - ${pack.name}]\n\n${physicsBlock}\n\nMETA_STYLES: ${pack.styles}\nMETA_EXCLUDE: ${pack.excludeStyles}\nMIX_TARGETS: ${pack.mixTargets}\n\n[END VOCALFORGE ARCHITECTURE]`;
}

function buildBlendedArchitecture(config: VocalConfiguration, screamBlock: string): string {
  if (!config.blendOutput) return '';
  const output = config.blendOutput;
  
  // Strip existing header from data to prevent double headers
  const cleanPhysics = output.physics.replace(/\{LAYER-1 VOCAL PHYSICS\}\n?/gi, '').trim();

  let physicsBlock = `{LAYER-1 VOCAL PHYSICS}\n`;
  physicsBlock += `[Vocal_Behavior: ${cleanPhysics}]\n`;
  physicsBlock += `[Lead_Vocal: ${output.voiceLead}]\n`;
  if (output.harmonyStack) physicsBlock += `[Harmony: ${output.harmonyStack}]\n`;
  if (output.gangVocals) physicsBlock += `[Choir: ${output.gangVocals}]\n`;
  if (screamBlock) physicsBlock += `\n${screamBlock}\n`;

  const deepDivePhysics = buildDeepDiveBlock(config.deepDive);
  if (deepDivePhysics) physicsBlock += `\n${deepDivePhysics}\n`;

  return `[VOCALFORGE BLEND ARCHITECTURE - ${output.name}]\n\n${physicsBlock}\n\nDESCRIPTION: ${output.description}\nMETA_STYLES: ${output.styles}\nMETA_EXCLUDE: ${output.excludeStyles}\nMIX_TARGETS: ${output.mixTargets}\n\nPACING (${config.blendConfig?.tempo || 95} BPM - ${output.pacing.label}):\n• Breathing: ${output.pacing.breathingCues}\n• Phrase Length: ${output.pacing.phraseLength}\n• Syllable Density: ${output.pacing.syllableDensity}\n• Rest Behavior: ${output.pacing.restBehavior}\n• Dynamics: ${output.pacing.dynamicRange}\n\n[END VOCALFORGE BLEND ARCHITECTURE]`;
}